(() => {
  'use strict';

  const configElement = document.getElementById('sidiz-marketing-measurement-config');
  if (!configElement) return;

  let config;

  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    console.error('[Marketing pixels] Invalid configuration.', error);
    return;
  }

  const debugEnabled = new URLSearchParams(window.location.search).get('sidiz_pixel_debug') === '1';
  const currency = config.currency || 'KRW';

  function debugLog(message, data) {
    if (!debugEnabled) return;
    console.info(`[Marketing pixels] ${message}`, data || '');
  }

  function getSiteType() {
    if (window.innerWidth <= 767) return 'm';
    if (window.innerWidth <= 1024) return 't';
    return 'd';
  }

  function pushCriteo(event) {
    window.criteo_q = window.criteo_q || [];
    window.criteo_q.push(
      { event: 'setAccount', account: Number(config.criteoAccountId) },
      {
        event: 'setEmail',
        email: config.customerEmailMd5 || '',
        hash_method: 'md5'
      },
      { event: 'setSiteType', type: getSiteType() },
      event
    );
    debugLog(`Criteo ${event.event}`, event);
  }

  function measureOpenAi(eventName, payload) {
    if (typeof window.oaiq !== 'function') {
      debugLog(`OpenAI ${eventName} skipped: SDK queue is unavailable.`);
      return;
    }

    window.oaiq('measure', eventName, payload);
    debugLog(`OpenAI ${eventName}`, payload);
  }

  function normalizeItem(item) {
    if (!item) return null;

    const id = String(item.id || '').trim();
    if (!id) return null;

    const rawPrice = Number(item.price);
    const rawQuantity = Number(item.quantity);

    return {
      id,
      name: String(item.name || id),
      price: Number.isFinite(rawPrice) && rawPrice >= 0 ? Math.round(rawPrice) : 0,
      quantity: Number.isFinite(rawQuantity) && rawQuantity > 0 ? Math.round(rawQuantity) : 1
    };
  }

  function normalizeItems(items) {
    if (!Array.isArray(items)) return [];
    return items.map(normalizeItem).filter(Boolean);
  }

  function createOpenAiContent(item, includeQuantity) {
    const content = {
      id: item.id,
      name: item.name,
      content_type: 'product',
      amount: item.price,
      currency
    };

    if (includeQuantity) content.quantity = item.quantity;
    return content;
  }

  function trackOpenAiPageView() {
    measureOpenAi('page_viewed', {
      type: 'contents',
      contents: [
        {
          id: window.location.pathname,
          name: document.title,
          content_type: 'page'
        }
      ]
    });
  }

  function trackProductView(product) {
    const item = normalizeItem(product);
    if (!item) {
      debugLog('Product view skipped: SKU is empty.');
      return;
    }

    pushCriteo({ event: 'viewItem', item: item.id });
    measureOpenAi('contents_viewed', {
      type: 'contents',
      contents: [createOpenAiContent(item, false)]
    });
  }

  function collectListItemIds() {
    const ids = [];

    function addId(value) {
      const id = String(value || '')
        .split(';')
        .map((candidate) => candidate.trim())
        .find(Boolean);

      if (id && !ids.includes(id)) ids.push(id);
    }

    document.querySelectorAll('[data-skulist]').forEach((element) => {
      addId(element.dataset.skulist);
    });

    if (ids.length < 3) {
      document.querySelectorAll('#search-data-container .products-data').forEach((element) => {
        try {
          const data = JSON.parse(element.textContent);
          const products = Array.isArray(data.products) ? data.products : [];
          products.forEach((product) => addId(product.sku));
        } catch (error) {
          debugLog('Search product data could not be parsed.', error);
        }
      });
    }

    return ids.slice(0, 3);
  }

  function trackListViewWhenReady() {
    let tracked = false;

    function track() {
      if (tracked) return true;

      const ids = collectListItemIds();
      if (!ids.length) return false;

      const event = { event: 'viewList', item: ids };
      if (config.searchKeyword) event.keywords = config.searchKeyword;

      pushCriteo(event);
      tracked = true;
      return true;
    }

    if (track()) return;

    const observer = new MutationObserver(() => {
      if (track()) observer.disconnect();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => {
      observer.disconnect();
      if (!tracked) debugLog('List view skipped: no product SKU was found.');
    }, 5000);
  }

  function trackCartView() {
    const items = normalizeItems(config.cartItems);
    if (!items.length) {
      debugLog('Basket view skipped: no product SKU was found.');
      return;
    }

    pushCriteo({
      event: 'viewBasket',
      item: items.map((item) => ({
        id: item.id,
        price: item.price,
        quantity: item.quantity
      }))
    });
  }

  function trackItemsAdded(items) {
    const normalizedItems = normalizeItems(items);
    if (!normalizedItems.length) {
      debugLog('Add-to-cart event skipped: no product SKU was found.');
      return;
    }

    pushCriteo({
      event: 'addToCart',
      item: normalizedItems.map((item) => ({
        id: item.id,
        price: item.price,
        quantity: item.quantity
      }))
    });

    measureOpenAi('items_added', {
      type: 'contents',
      amount: normalizedItems.reduce((total, item) => total + item.price * item.quantity, 0),
      currency,
      contents: normalizedItems.map((item) => createOpenAiContent(item, true))
    });
  }

  document.addEventListener('sidiz:marketing-items-added', (event) => {
    trackItemsAdded(event.detail && event.detail.items);
  });

  function initializePageTracking() {
    trackOpenAiPageView();

    if (config.isHome) {
      pushCriteo({ event: 'viewHome' });
      return;
    }

    if (config.pageType === 'product') {
      trackProductView(config.product);
      return;
    }

    if (config.pageType === 'cart') {
      trackCartView();
      return;
    }

    if (config.isList) trackListViewWhenReady();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializePageTracking, { once: true });
  } else {
    initializePageTracking();
  }
})();
